/* eslint-disable no-undef */
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import logoIcon from '../../assets/images/LogoIcon.svg';
// import menu from '../Data/menu.json';
// import menu2 from '../Data/menu2.json';
// import adminMenu from '../Data/adminMenu.json';
import sideMenu from '../Data/sidebarMenu.json';

// import employeeMenu from '../Data/employMenu.json';
// import techLeadMenu from '../Data/techLeadMenu.json';
// import managementMenu from '../Data/managementMenu.json';

import { useIsLoggedInQuery, useUserLogOutMutation } from '../../redux/apis/authApi';
import { persistor } from '../../redux/store';

import { notification } from '../../utils/notification';
import { getUserName, withoutProfilePicture } from '../../utils/handler';
import {
  GLOBAL,
  ROLES,
  WORK_SHOW_STORAGE_KEY,
  normalizeUserLocality,
  getWorkShowIdByLocality
} from '../../utils/constant/global';
import { setSelectedOffice, selectSelectedOfficeId } from '../../redux/Slice/officeSlice';
import { resetAllApiCaches } from '../../redux/utils/resetApiCaches';
import { TOAST_TYPE } from '../../utils/constant/errorMessage';
import { LB_HEADER } from '../../utils/constant/labels';

import SidebarNotifications from './SidebarNotifications';
import SidebarWorkShow from './SidebarWorkShow';
import ConfirmDialog from './ConfirmDialog';

import './SidebarUserPanel.scss';
import './Sidebar.scss';

const { ADMIN, HR, ACCOUNTANT } = ROLES;
const { TOAST_ERROR, TOAST_SUCCESS } = TOAST_TYPE;
const { LB_SIGN_OUT } = LB_HEADER;

const SIDEBAR_USER_MENU_POPPER = {
  strategy: 'fixed',
  modifiers: [
    { name: 'preventOverflow', options: { padding: 12 } },
    { name: 'offset', options: { offset: [0, 10] } }
  ]
};

function Sidebar(props) {
  const { data } = useIsLoggedInQuery();
  const [menuData, setMenuData] = useState([]);
  // const [darkLightMode, setDarkLightMode] = useState('light');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const dispatch = useDispatch();
  const selectedWorkShow = useSelector(selectSelectedOfficeId);
  const syncedAdminOfficeRef = useRef(null);

  // This State is not in used that's why commented.
  // const [updateRtl, setUpdateRtl] = useState(false);

  const { PROJECT_NAME, PROFILE } = GLOBAL;
  const navigate = useNavigate();
  const [userLogOut] = useUserLogOutMutation();

  useEffect(() => {
    window.document.children[0].setAttribute('data-theme', 'light');
  }, []);

  useEffect(() => {
    const loginUser = data?.data;
    const roleName = loginUser?.hrxRole?.roleName;
    // Admin + Accountant use selected office for API locality/TZ — sync from profile on login
    // so AU-only users are not stuck on default Ahmedabad (IN).
    if (!loginUser?._id || ![ADMIN, ACCOUNTANT].includes(roleName)) return;
    if (syncedAdminOfficeRef.current === loginUser._id) return;

    syncedAdminOfficeRef.current = loginUser._id;
    const officeId = getWorkShowIdByLocality(loginUser.locality ?? loginUser.nationality);

    if (selectedWorkShow !== officeId) {
      dispatch(setSelectedOffice(officeId));
      resetAllApiCaches(dispatch);
    }
    localStorage.setItem(WORK_SHOW_STORAGE_KEY, officeId);
  }, [data?.data, dispatch, selectedWorkShow]);

  const normalizeName = (name = '') => name.toLowerCase().replace(/\s+/g, '').trim();

  const createModuleMap = (modules = []) => {
    const map = {};
    modules.forEach((mod) => {
      map[mod._id] = mod;
    });
    return map;
  };
  const filterMenuByModules = (menuList = [], modules = []) => {
    const moduleMap = createModuleMap(modules);
    const activeModules = modules.filter((mod) => mod?.isActive !== false);

    return menuList
      .map((menu) => {
        const menuName = normalizeName(menu.name);
        const menuPath = String(menu?.routerLink?.[0] || '')
          .replace(/^\//, '')
          .toLowerCase();

        // Match by module name, or by path (e.g. /time-edit-requests)
        const matchedModule = activeModules.find((mod) => {
          if (normalizeName(mod.name) === menuName) return true;
          const modPath = String(mod.path || '')
            .replace(/^\//, '')
            .toLowerCase();
          return Boolean(menuPath) && Boolean(modPath) && modPath === menuPath;
        });

        // ❌ if module not present / inactive → hide
        if (!matchedModule) return null;

        // ❌ if parentId exists but parent module not present → hide
        if (matchedModule.parentId && !moduleMap[matchedModule.parentId]) {
          return null;
        }

        // 🔽 filter children
        let filteredChildren = [];
        if (menu.children?.length) {
          filteredChildren = filterMenuByModules(menu.children, modules);
        }

        return {
          ...menu,
          children: filteredChildren,
          isToggled: filteredChildren.length > 0
        };
      })
      .filter(Boolean);
  };

  const isTimeDoctorMenuItem = (item) =>
    item?.name === 'Time Doctor' || item?.routerLink?.[0] === 'attendance-employees';

  const getTimeDoctorMenuItem = () => {
    const attendanceMenu = sideMenu.menu.find((item) => item.name === 'Attendance');
    return attendanceMenu?.children?.find((child) => isTimeDoctorMenuItem(child)) || null;
  };

  const getAttendanceMenuTemplate = () =>
    sideMenu.menu.find((item) => item.name === 'Attendance') || null;

  /**
   * Time Doctor tab:
   * - Admin → always show (no isTimedoctorUser / timeDoctorUserId check)
   * - Others → only when isTimedoctorUser + timeDoctorUserId
   * Role module permission is ignored for this item.
   */
  const applyTimeDoctorMenuVisibility = (menuList = [], user = {}) => {
    const isAdmin = user?.hrxRole?.roleName === ADMIN;
    const canSeeTimeDoctor =
      isAdmin || (Boolean(user?.isTimedoctorUser) && Boolean(user?.timeDoctorUserId));
    const timeDoctorItem = getTimeDoctorMenuItem();
    const attendanceTemplate = getAttendanceMenuTemplate();

    const mergeAttendanceChildren = (existingChildren = []) => {
      const withoutTimeDoctor = existingChildren.filter((child) => !isTimeDoctorMenuItem(child));
      if (!canSeeTimeDoctor || !timeDoctorItem || !attendanceTemplate?.children?.length) {
        return withoutTimeDoctor;
      }

      // Preserve sidebarMenu.json order for Attendance children
      return attendanceTemplate.children
        .map((templateChild) => {
          if (isTimeDoctorMenuItem(templateChild)) return timeDoctorItem;
          return withoutTimeDoctor.find((child) => child.name === templateChild.name) || null;
        })
        .filter(Boolean);
    };

    let nextMenu = menuList.map((item) => {
      if (!item?.children?.length && item.name !== 'Attendance') return item;

      if (item.name !== 'Attendance') {
        if (!item?.children?.length) return item;
        const children = item.children.filter((child) => !isTimeDoctorMenuItem(child));
        return {
          ...item,
          children,
          isToggled: children.length > 0
        };
      }

      const children = mergeAttendanceChildren(item.children || []);
      return {
        ...item,
        children,
        isToggled: children.length > 0
      };
    });

    // Flags true but Attendance parent missing from role modules → still show Time Doctor
    if (canSeeTimeDoctor && timeDoctorItem && attendanceTemplate) {
      const hasAttendance = nextMenu.some((item) => item.name === 'Attendance');
      if (!hasAttendance) {
        nextMenu = [
          ...nextMenu,
          {
            ...attendanceTemplate,
            children: [timeDoctorItem],
            isToggled: true
          }
        ];
      }
    }

    // Drop empty Attendance group if Time Doctor was removed and no other children remain
    return nextMenu.filter(
      (item) => item.name !== 'Attendance' || (item.children && item.children.length > 0)
    );
  };

  // AU-only menu items. Each entry is hidden unless the user meets its rule,
  // regardless of role-module grants:
  //  - Guest Check-In : AU users with an Admin/HR role
  //  - Room Booking   : any AU user
  const applyAuOnlyVisibility = (menuList = [], user = {}) => {
    const roleName = user?.hrxRole?.roleName;
    const userLocality = normalizeUserLocality(user?.locality ?? user?.nationality);
    const isAu = userLocality.includes('AU');

    const rules = {
      'Guest Check-In': isAu && [ADMIN, HR].includes(roleName),
      'Room Booking': isAu
    };

    return menuList.filter((item) => {
      const name = normalizeName(item.name);
      const match = Object.keys(rules).find((key) => normalizeName(key) === name);
      // Not an AU-gated item -> keep it. Gated item -> keep only if its rule passes.
      return match === undefined ? true : rules[match];
    });
  };

  useEffect(() => {
    if (!data?.data?.hrxRole) return;

    const modules = data.data.hrxRole.modules || [];

    const filteredMenu = filterMenuByModules(sideMenu.menu, modules);

    const withTimeDoctor = applyTimeDoctorMenuVisibility(filteredMenu, data.data);
    setMenuData(applyAuOnlyVisibility(withTimeDoctor, data.data));
  }, [data]);

  // useEffect(() => {
  //   if (data?.data?.role?.roleName === ADMIN) {
  //     setMenuData(adminMenu?.menu3);
  //     console.log("adminMenu?.menu3", adminMenu?.menu3);

  //   } else if (data?.data?.role?.roleName === LEAD) {
  //     setMenuData(techLeadMenu?.menu3);
  //   } else if (data?.data?.role?.roleName === MANAGEMENT) {
  //     setMenuData(managementMenu?.menu3);
  //   } else {
  //     setMenuData(employeeMenu?.menu3);
  //   }
  // }, [data]);

  function openChildren(id) {
    var otherTabs = document.getElementsByClassName('has-children');
    if (otherTabs) {
      for (var i = 0; i < otherTabs.length; i++) {
        if (otherTabs[i].id !== id) {
          otherTabs[i].className = otherTabs[i].className.replace(' show', '');
          if (otherTabs[i].parentElement.children.length > 1) {
            otherTabs[i].parentElement.children[0].setAttribute('aria-expanded', 'false');
          }
        }
      }
    }
    var menutab = document.getElementById(id);
    if (menutab) {
      if (menutab.classList.contains('show')) {
        menutab.classList.remove('show');
        if (menutab.parentElement.children.length > 1) {
          menutab.parentElement.children[0].setAttribute('aria-expanded', 'false');
        }
      } else {
        menutab.classList.add('show');
        if (menutab.parentElement.children.length > 1) {
          menutab.parentElement.children[0].setAttribute('aria-expanded', 'true');
        }
      }
    }
  }
  function openChildren1(id) {
    var otherTabs = document.getElementsByClassName('has-children');
    if (otherTabs) {
      for (var i = 0; i < otherTabs.length; i++) {
        otherTabs[i].className = otherTabs[i].className.replace(' show', '');
      }
    }
    var menutab = document.getElementById(id);
    if (menutab) {
      menutab.classList.add('show');
      if (menutab.parentElement.children.length > 1) {
        menutab.parentElement.children[0].setAttribute('aria-expanded', 'true');
      }
    }
  }

  // This function is not in used that's why commented.

  // function closeChildren() {
  //   var otherTabs = document.getElementsByClassName('has-children');
  //   if (otherTabs) {
  //     for (var i = 0; i < otherTabs.length; i++) {
  //       otherTabs[i].className = otherTabs[i].className.replace(' show', '');
  //       if (otherTabs[i].parentElement.children.length > 1) {
  //         otherTabs[i].parentElement.children[0].setAttribute('aria-expanded', 'false');
  //       }
  //     }
  //   }
  // }

  function GotoChangeMenu(val) {
    navigate(`${process.env.PUBLIC_URL + '/' + val}`);
  }

  /*  function onChangeDarkMode() {
    if (window.document.children[0].getAttribute('data-theme') === 'light') {
      window.document.children[0].setAttribute('data-theme', 'dark');
      setDarkLightMode('dark');
    } else {
      window.document.children[0].setAttribute('data-theme', 'light');
      setDarkLightMode('light');
    }
  } */

  // This function is not in used that's why commented.

  // function onChangeRTLMode() {
  //   if (document.body.classList.contains('rtl_mode')) {
  //     document.body.classList.remove('rtl_mode');
  //   } else {
  //     document.body.classList.add('rtl_mode');
  //   }
  //   setUpdateRtl(!updateRtl);
  // }

  const activeList = (path) => {
    const current = String(window.location.pathname || '').replace(/\/+$/, '') || '/';
    const target = `${process.env.PUBLIC_URL}/${path}`.replace(/\/+$/, '') || '/';
    const isActive = current === target || current.startsWith(`${target}/`);
    return isActive ? 'm-link active' : 'm-link';
  };

  const activeChildList = (d) => {
    const cl =
      d.children.filter((d) => '/' + d.routerLink[0] === window.location.pathname).length > 0
        ? 'active'
        : '';

    return cl;
  };

  const handleLogout = async () => {
    try {
      const res = await userLogOut({
        emailMobile: data?.data?.email
      });
      if (res?.data?.success) {
        localStorage.removeItem(WORK_SHOW_STORAGE_KEY);
        persistor.pause();
        persistor.flush().then(() => {
          return persistor.purge();
        });
        window.history.replaceState(null, '', '/auth');
        window.location.reload();
        notification(res?.data?.statusMessage, TOAST_SUCCESS);
      }
    } catch (error) {
      notification(error.message, TOAST_ERROR);
    }
  };

  const { activekey, isSidebarMini, setIsSidebarMini } = props;

  const loginUser = data?.data;
  const roleName = loginUser?.hrxRole?.roleName;
  const canChooseOffice = roleName === ADMIN || roleName === ACCOUNTANT;
  const userLocality = normalizeUserLocality(loginUser?.locality ?? loginUser?.nationality);
  // Show for Admin/Accountant with IN and/or AU — previously IN-only hid the selector for AU users
  const showOfficeSelector =
    canChooseOffice && (userLocality.includes('IN') || userLocality.includes('AU'));
  const displayName = getUserName(loginUser) || 'User';
  const userEmail = loginUser?.email || '';
  const userAvatar = loginUser?.profilePicture || withoutProfilePicture({ name: displayName });

  const confirmSignOut = () => {
    setShowLogoutConfirm(false);
    handleLogout();
  };

  const handleWorkShowChange = (workShowId) => {
    if (workShowId === selectedWorkShow) return;
    dispatch(setSelectedOffice(workShowId));
    localStorage.setItem(WORK_SHOW_STORAGE_KEY, workShowId);
    resetAllApiCaches(dispatch);
  };

  return (
    <div
      id="mainSideMenu"
      className={`sidebar me-0 sidebar--inner-scroll ${isSidebarMini ? 'sidebar-mini' : ''}`}
    >
      <div className="sidebar-inner-stack ">
        <div className="flex-shrink-0">
          <button
            type="button"
            className={`sidebar-edge-toggle${isSidebarMini ? ' sidebar-edge-toggle--mini' : ''}`}
            onClick={() => {
              setIsSidebarMini(!isSidebarMini);
            }}
            aria-label={isSidebarMini ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <i className={isSidebarMini ? 'icofont-rounded-right' : 'icofont-rounded-left'} />
          </button>
        </div>

        <div
          // onClick={() => {
          //   if (data?.data?.roleId?.roleName === ADMIN) {
          //     navigate('/admin/employees');
          //   } else {
          //     navigate('/employee');
          //   }
          // }}
          style={{ margin: '10px 0px', cursor: 'pointer' }}
          className="mb-0 brand-icon px-4 pb-1 flex-shrink-0"
        >
          <span className="logo-icon">
            <img src={logoIcon} alt="logo" style={{ height: '50%' }} />
          </span>
          <span className="logo-text-wrap">
            <span className="logo-text">{PROJECT_NAME}</span>
            <span className="logo-tagline">Software Co</span>
          </span>
        </div>

        {showOfficeSelector ? (
          <SidebarWorkShow
            selectedWorkShow={selectedWorkShow}
            onWorkShowChange={handleWorkShowChange}
            isSidebarMini={isSidebarMini}
          />
        ) : null}

        <div className="sidebar-menu-scroll hide-scrollbar mt-2">
          <ul className="menu-list px-4">
            {menuData.map((d, i) => {
              if (!d.isToggled) {
                return (
                  <li
                    key={'shsdg' + i}
                    className={activeList(d.routerLink[0])}
                    style={{ cursor: 'pointer' }}
                    title={d.name}
                    onClick={() => {
                      GotoChangeMenu(d.routerLink[0]);
                    }}
                  >
                    <i className={d.iconClass}></i>
                    <span>{d.name}</span>
                  </li>
                );
              }
              if (d.children.length === 0) {
                return (
                  <li key={'dsfshsdg' + i} className=" collapsed">
                    <Link
                      to={`${process.env.PUBLIC_URL}` + '/' + d.routerLink[0]}
                      className={`m-link ${d.routerLink[0] === activekey ? 'active' : ''}`}
                      title={d.name}
                    >
                      <i className={d.iconClass}></i>
                      <span>{d.name}</span>
                      <span className="arrow icofont-dotted-down ms-auto text-end fs-5"></span>
                    </Link>
                  </li>
                );
              }
              return (
                <li key={'shsdg' + i} className=" collapsed">
                  <a
                    className={`m-link ${activeChildList(d)}`}
                    href="#!"
                    title={d.name}
                    onClick={(e) => {
                      e.preventDefault();
                      openChildren('menu-Pages' + i);
                    }}
                  >
                    <i className={d.iconClass}></i>
                    <span>{d.name}</span>
                    <span className="arrow icofont-dotted-down ms-auto text-end fs-5"></span>
                  </a>
                  {d.children.length > 0 ? (
                    <ul className="sub-menu collapse has-children" id={'menu-Pages' + i}>
                      {d.children.map((data, ind) => {
                        if (d.children.length > 0) {
                          if (activekey === '/' + data.routerLink[0]) {
                            setTimeout(() => {
                              openChildren1('menu-Pages' + i);
                            }, 500);
                          }
                        }
                        return (
                          <li key={'jfdgj' + ind}>
                            <Link
                              className={
                                window.location.pathname === `/${data.routerLink[0]}`
                                  ? 'ms-link active'
                                  : 'ms-link'
                              }
                              to={`${process.env.PUBLIC_URL}` + '/' + data.routerLink[0]}
                              title={data.name}
                            >
                              {' '}
                              <span>{data.name}</span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </li>
              );
            })}
            <li className="sidebar-menu-notifications-entry">
              <SidebarNotifications isSidebarMini={isSidebarMini} variant="menuItem" />
            </li>
          </ul>
        </div>
        {/* <ul className="list-unstyled mb-0">
          <li className="d-flex align-items-center justify-content-center">
            <div className="form-check form-switch theme-switch">
              <input
                className="form-check-input"
                type="checkbox"
                checked={darkLightMode === 'dark' ? true : false}
                id="theme-switch"
                onChange={() => {
                  onChangeDarkMode();
                }}
              />
              <label className="form-check-label" htmlFor="theme-switch">
                Enable Dark Mode!
              </label>
            </div>
          </li>

          {/* This RTL is not in used that's why commented. */}

        {/*<li className="d-flex align-items-center justify-content-center">
            <div className="form-check form-switch theme-rtl">
              <input
                className="form-check-input"
                type="checkbox"
                checked={document.body.classList.contains('rtl_mode')}
                id="theme-rtl"
                onChange={() => {
                  onChangeRTLMode();
                }}
              />
              <label className="form-check-label" htmlFor="theme-rtl">
                Enable RTL Mode!
              </label>
            </div>
          </li> 
        </ul>*/}
        <button type="button" className="btn sidebar-mini-btn text-light flex-shrink-0"></button>
      </div>

      <div
        className={`sidebar-user-panel flex-shrink-0 ${isSidebarMini ? 'sidebar-user-panel--mini' : ''}`}
      >
        {!isSidebarMini ? (
          <Dropdown
            drop="up"
            align="start"
            popperConfig={SIDEBAR_USER_MENU_POPPER}
            className="sidebar-user-panel__dropdown"
          >
            <Dropdown.Toggle
              variant="link"
              id="sidebar-user-menu"
              className="sidebar-user-panel__toggle text-decoration-none shadow-none"
            >
              <img src={userAvatar} alt="" className="sidebar-user-panel__avatar" />
              <div className="sidebar-user-panel__meta">
                <span className="sidebar-user-panel__name" title={displayName}>
                  {displayName}
                </span>
                {userEmail ? (
                  <span className="sidebar-user-panel__email" title={userEmail}>
                    {userEmail}
                  </span>
                ) : null}
              </div>
              <i className="icofont-rounded-down sidebar-user-panel__caret" aria-hidden />
            </Dropdown.Toggle>
            <Dropdown.Menu className="sidebar-user-panel__menu shadow border-0">
              {[ADMIN, HR].includes(loginUser?.hrxRole?.roleName) ? (
                <>
                  <Dropdown.Item
                    className="sidebar-user-panel__menu-item d-flex align-items-center gap-2 py-2"
                    onClick={() => navigate(`${process.env.PUBLIC_URL}/profile`)}
                  >
                    <i className="icofont-user-alt-3" aria-hidden />
                    <span>{PROFILE}</span>
                  </Dropdown.Item>
                  <Dropdown.Divider />
                </>
              ) : null}
              <Dropdown.Item
                className="sidebar-user-panel__menu-item d-flex align-items-center gap-2 py-2"
                onClick={() => setShowLogoutConfirm(true)}
              >
                <i className="icofont-logout text-danger" aria-hidden />
                <span>{LB_SIGN_OUT}</span>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        ) : (
          <Dropdown
            drop="end"
            align="end"
            popperConfig={SIDEBAR_USER_MENU_POPPER}
            className="sidebar-user-panel__dropdown sidebar-user-panel__dropdown--mini"
          >
            <Dropdown.Toggle
              variant="link"
              id="sidebar-user-menu-mini"
              className="sidebar-user-panel__toggle sidebar-user-panel__toggle--mini text-decoration-none shadow-none p-0"
              aria-label="Account menu"
            >
              <img src={userAvatar} alt="" className="sidebar-user-panel__avatar" />
            </Dropdown.Toggle>
            <Dropdown.Menu className="sidebar-user-panel__menu shadow border-0">
              <Dropdown.Header className="small text-wrap">
                <div className="fw-semibold">{displayName}</div>
                <div className="text-muted fw-normal text-truncate">{userEmail}</div>
              </Dropdown.Header>
              <Dropdown.Divider />
              {[ADMIN, HR].includes(loginUser?.hrxRole?.roleName) ? (
                <Dropdown.Item
                  className="sidebar-user-panel__menu-item d-flex align-items-center gap-2 py-2"
                  onClick={() => navigate(`${process.env.PUBLIC_URL}/profile`)}
                >
                  <i className="icofont-user-alt-3" aria-hidden />
                  <span>{PROFILE}</span>
                </Dropdown.Item>
              ) : null}
              {[ADMIN, HR].includes(loginUser?.hrxRole?.roleName) ? <Dropdown.Divider /> : null}
              <Dropdown.Item
                className="sidebar-user-panel__menu-item d-flex align-items-center gap-2 py-2"
                onClick={() => setShowLogoutConfirm(true)}
              >
                <i className="icofont-logout text-danger" aria-hidden />
                <span>{LB_SIGN_OUT}</span>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>

      <ConfirmDialog
        show={showLogoutConfirm}
        onHide={() => setShowLogoutConfirm(false)}
        onConfirm={confirmSignOut}
        variant="danger"
        icon="icofont-logout"
        title="Sign out?"
        message="Are you sure you want to log out? You'll need to sign in again to access your HRX account."
        confirmLabel={LB_SIGN_OUT}
        cancelLabel="Cancel"
      />
    </div>
  );
}

export default Sidebar;
